import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Plus, X, RotateCw, Lock } from "lucide-react";
import { apiKeysApi } from "@/lib/account";


function StatTile({ label, value, sub, tone = "default", testid }) {
  const valueTone = {
    accent: "text-[var(--accent)]",
    warn: "text-yellow-400",
    muted: "text-[var(--text-muted)]",
    default: "text-[var(--text)]",
  }[tone] || "text-[var(--text)]";
  return (
    <div
      data-testid={testid}
      className="border border-[var(--border)] p-4"
    >
      <div className="font-mono-ui text-[9px] tracking-[0.26em] uppercase text-[var(--text-dim)] mb-2">
        {label}
      </div>
      <div className={`font-serif text-3xl leading-none ${valueTone}`}>{value}</div>
      {sub && (
        <div className="font-mono-ui text-[10px] tracking-[0.16em] uppercase text-[var(--text-dim)] mt-2.5 leading-relaxed">
          {sub}
        </div>
      )}
    </div>
  );
}

/**
 * Admin section: manage the API-keys email allowlist.
 *
 * Two tiers shown:
 *   • Static base (env / code-driven, read-only here) — the founder + 4
 *     ops emails that always have access. Admin cannot remove these from
 *     this UI; rotation requires an env change + redeploy.
 *   • DB invites — the dynamic list managed by the admin. Add an email,
 *     it gets a Resend welcome, and revoke kills both the allowlist
 *     entry AND any API keys the invitee already created.
 */
export default function AdminApiAllowlistSection() {
  const [data, setData] = useState(null);
  const [journey, setJourney] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [list, j] = await Promise.all([
        apiKeysApi.adminListAllowlist(),
        apiKeysApi.adminJourney(),
      ]);
      setData(list);
      setJourney(j);
    } catch (e) {
      toast.error(e.message || "Failed to load allowlist");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onInvite = async (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v || !v.includes("@") || busy) return;
    setBusy(true);
    try {
      const r = await apiKeysApi.adminAddInvite(v);
      if (r.email_sent) {
        toast.success(`Invite sent to ${v}`);
      } else {
        toast.info(
          `Invite saved for ${v}, but email failed: ${r.email_reason || "unknown"}. They still have access; you can resend.`,
        );
      }
      setEmail("");
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (id, address) => {
    if (
      !confirm(
        `Revoke API-beta access for ${address}? This will also delete every API key they currently hold.`,
      )
    )
      return;
    setBusyId(id);
    try {
      const r = await apiKeysApi.adminRevokeInvite(id);
      toast.success(
        r.keys_cascaded > 0
          ? `Access revoked. ${r.keys_cascaded} API key(s) cascaded.`
          : "Access revoked.",
      );
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to revoke");
    } finally {
      setBusyId(null);
    }
  };

  const onResend = async (id) => {
    setBusyId(id);
    try {
      const r = await apiKeysApi.adminResendInvite(id);
      if (r.email_sent) toast.success("Invite re-sent.");
      else toast.error(`Resend failed: ${r.email_reason || "unknown"}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to resend");
    } finally {
      setBusyId(null);
    }
  };

  if (!data) {
    return (
      <div data-testid="admin-allowlist-loading" className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
        Loading allowlist…
      </div>
    );
  }

  const activeInvites = (data.invites || []).filter((i) => i.status === "active");
  const revokedInvites = (data.invites || []).filter((i) => i.status === "revoked");
  const journeyByEmail = new Map(
    (journey?.per_invite || []).map((p) => [p.email, p]),
  );

  const fmtMin = (m) => {
    if (m === null || m === undefined) return "—";
    if (m < 60) return `${Math.round(m)}m`;
    const h = m / 60;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };

  return (
    <div data-testid="admin-allowlist-section" className="space-y-8">
      {/* Journey analytics strip */}
      {journey && journey.summary.total_invites > 0 && (
        <div
          data-testid="allowlist-journey-strip"
          className="grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          <StatTile
            testid="journey-conversion"
            label="Activation"
            value={`${Math.round((journey.summary.conversion_rate || 0) * 100)}%`}
            sub={`${journey.summary.activated} of ${journey.summary.total_invites} invitees created a key`}
            tone={journey.summary.conversion_rate >= 0.5 ? "accent" : journey.summary.activated === 0 ? "muted" : "default"}
          />
          <StatTile
            testid="journey-ttfk"
            label="Time to first key"
            value={fmtMin(journey.time_to_first_key.median_minutes)}
            sub={
              journey.time_to_first_key.n
                ? `median across ${journey.time_to_first_key.n} · avg ${fmtMin(journey.time_to_first_key.average_minutes)}`
                : "no activations yet"
            }
          />
          <StatTile
            testid="journey-zombies"
            label="Zombies"
            value={`${journey.summary.zombies}`}
            sub={
              journey.summary.zombies
                ? `active >7d, no key — nudge or revoke`
                : "all active invites are warm"
            }
            tone={journey.summary.zombies > 0 ? "warn" : "muted"}
          />
          <StatTile
            testid="journey-revoked"
            label="Revoked · 30d"
            value={`${journey.summary.revoked_30d}`}
            sub={`${journey.summary.active} active right now`}
            tone="muted"
          />
        </div>
      )}
      {/* Header + invite form */}
      <div>
        <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-1">
          Beta Invites
        </div>
        <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed mb-5 max-w-2xl">
          Email gets <em>API keys</em> + <em>Usage</em> in their account menu, the right
          to create programmatic keys, and an automated welcome from Resend. Revoke
          here cuts off both the gate and any existing keys in one step.
        </p>

        <form onSubmit={onInvite} className="flex gap-2 items-stretch max-w-xl">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="invitee@example.com"
            data-testid="allowlist-email-input"
            className="flex-1 bg-[var(--bg-elevated)] border border-[var(--border)] px-3 py-2 font-mono-ui text-xs text-[var(--text)] placeholder:text-[var(--text-dim)] focus:outline-none focus:border-[var(--accent)] transition-colors"
          />
          <button
            type="submit"
            disabled={busy || !email.includes("@")}
            data-testid="allowlist-invite-btn"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            <Plus size={12} /> {busy ? "Sending…" : "Invite"}
          </button>
        </form>
      </div>

      {/* Active invites */}
      <div>
        <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-3">
          Active ({activeInvites.length})
        </div>
        {activeInvites.length === 0 ? (
          <div className="border border-dashed border-[var(--border)] px-4 py-6 font-mono-ui text-[11px] tracking-[0.18em] uppercase text-[var(--text-dim)] text-center">
            No invites yet. Type an email above to start the beta.
          </div>
        ) : (
          <div className="border border-[var(--border)]">
            {activeInvites.map((i, idx) => {
              const j = journeyByEmail.get(i.email);
              const isZombie = !!j?.is_zombie;
              return (
                <div
                  key={i.id}
                  data-testid={`allowlist-row-${i.email}`}
                  className={`flex items-center justify-between px-4 py-3 ${idx > 0 ? "border-t border-[var(--border-soft)]" : ""}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <Mail size={14} className="text-[var(--text-dim)] shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono-ui text-xs text-[var(--text)] truncate">{i.email}</span>
                        {j && j.keys_issued > 0 && (
                          <span
                            data-testid={`allowlist-badge-keys-${i.email}`}
                            className="font-mono-ui text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5 border border-[var(--accent)] text-[var(--accent)]"
                          >
                            {j.keys_issued} key{j.keys_issued > 1 ? "s" : ""}
                            {j.minutes_to_first_key !== null ? ` · ${fmtMin(j.minutes_to_first_key)}` : ""}
                          </span>
                        )}
                        {isZombie && (
                          <span
                            data-testid={`allowlist-badge-zombie-${i.email}`}
                            className="font-mono-ui text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5 border border-yellow-500/50 text-yellow-400"
                          >
                            Zombie · {fmtMin((Date.now() - new Date(i.invited_at).getTime()) / 60000)}
                          </span>
                        )}
                      </div>
                      <div className="font-mono-ui text-[10px] tracking-[0.16em] uppercase text-[var(--text-dim)] mt-1">
                        Invited by {i.invited_by_email || "—"}
                        {i.email_sent ? " · email delivered" : i.email_reason ? " · email failed" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center shrink-0">
                    <button
                      onClick={() => onResend(i.id)}
                      disabled={busyId === i.id}
                      data-testid={`allowlist-resend-${i.email}`}
                      title="Resend invite email"
                      className="font-mono-ui text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors inline-flex items-center gap-1.5"
                    >
                      <RotateCw size={11} /> Resend
                    </button>
                    <button
                      onClick={() => onRevoke(i.id, i.email)}
                      disabled={busyId === i.id}
                      data-testid={`allowlist-revoke-${i.email}`}
                      className="font-mono-ui text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-[var(--border)] text-[var(--text-muted)] hover:border-red-500/60 hover:text-red-400 disabled:opacity-40 transition-colors inline-flex items-center gap-1.5"
                    >
                      <X size={11} /> Revoke
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Static base — read-only */}
      <div>
        <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-3">
          Static base ({data.static_base?.length || 0}) — env-driven
        </div>
        <div className="border border-[var(--border)]">
          {(data.static_base || []).map((e, idx) => (
            <div
              key={e}
              className={`flex items-center gap-3 px-4 py-2.5 ${idx > 0 ? "border-t border-[var(--border-soft)]" : ""}`}
            >
              <Lock size={12} className="text-[var(--text-dim)]" />
              <span className="font-mono-ui text-xs text-[var(--text-muted)]">{e}</span>
            </div>
          ))}
        </div>
        <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
          To edit, set <code className="text-[var(--text-muted)]">API_KEYS_EMAIL_ALLOWLIST</code> in .env and redeploy.
        </p>
      </div>

      {/* Revoked history (if any) */}
      {revokedInvites.length > 0 && (
        <details className="border border-[var(--border)] px-4 py-3">
          <summary className="cursor-pointer font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)]">
            Revoked history ({revokedInvites.length})
          </summary>
          <div className="mt-3 space-y-1.5">
            {revokedInvites.map((i) => (
              <div
                key={i.id}
                className="font-mono-ui text-[11px] text-[var(--text-dim)] flex items-baseline gap-3"
              >
                <span className="text-[var(--text-muted)]">{i.email}</span>
                <span>revoked {i.revoked_at ? new Date(i.revoked_at).toLocaleDateString() : ""}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
